/** Public flashers and download pages stay on this host. */
export const PUBLIC_HOST = 'firmware.fluoraplant.com';

/** Admin UI (login, devices, users, …) lives on this host. */
export const ADMIN_HOST = 'server.fluoraplant.com';

/** Beta program portal (welcome, account, my devices). */
export const BETA_HOST = 'beta.fluoraplant.com';
const LOCAL_BETA_HOSTS = ['beta.localhost'];

/** Internal testing portal for staff and development devices. */
export const TESTING_HOST = 'testing.fluoraplant.com';
const LOCAL_TESTING_HOSTS = ['testing.localhost'];

/** Store metrics dashboard (Shopify + Meta). */
export const DASHBOARD_HOST = 'dashboard.fluoraplant.com';
const LOCAL_DASHBOARD_HOSTS = ['dashboard.localhost'];

export function isBetaHost(hostname = window.location.hostname): boolean {
  return hostname === BETA_HOST || hostname === `www.${BETA_HOST}` || LOCAL_BETA_HOSTS.includes(hostname);
}

export function isTestingHost(hostname = window.location.hostname): boolean {
  return hostname === TESTING_HOST || hostname === `www.${TESTING_HOST}` || LOCAL_TESTING_HOSTS.includes(hostname);
}

export function isDashboardHost(hostname = window.location.hostname): boolean {
  return hostname === DASHBOARD_HOST || hostname === `www.${DASHBOARD_HOST}` || LOCAL_DASHBOARD_HOSTS.includes(hostname);
}

export function dashboardUrl(path = '/'): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://${DASHBOARD_HOST}${normalized}`;
}

export function adminUrl(path = '/'): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://${ADMIN_HOST}${normalized}`;
}

export function isAdminHost(hostname = window.location.hostname): boolean {
  return hostname === ADMIN_HOST || hostname === `www.${ADMIN_HOST}`;
}

export const PUBLIC_PATHS = [
  '/update',
  '/beta',
  '/download',
  '/legacy',
  '/releases',
  '/mobile-app',
] as const;

export function normalizeAppPath(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

export function isPublicPath(pathname: string): boolean {
  const path = normalizeAppPath(pathname);
  return (PUBLIC_PATHS as readonly string[]).includes(path);
}

export function publicUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://${PUBLIC_HOST}${normalized}`;
}

export function betaUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://${BETA_HOST}${normalized}`;
}

export function deployTestPublicPath(test: { slug?: string; name: string }): string {
  const source = String(test.slug || test.name || '').trim();
  const slug = source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'test';
  return `/tests/${encodeURIComponent(slug)}`;
}

export function publicDeployTestUrl(test: { slug?: string; name: string }): string {
  return betaUrl(deployTestPublicPath(test));
}

export function testingUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://${TESTING_HOST}${normalized}`;
}

export function publicInternalTestUrl(test: { slug?: string; name: string }): string {
  return testingUrl(deployTestPublicPath(test));
}

export function hostRedirectUrl(
  hostname: string,
  pathname: string,
  search = '',
  hash = '',
): string | null {
  if (hostname !== PUBLIC_HOST && hostname !== ADMIN_HOST) {
    return null;
  }

  const normalizedPath = normalizeAppPath(pathname);
  if (normalizedPath === '/tests' || normalizedPath.startsWith('/tests/')) {
    return null;
  }

  const publicPage = isPublicPath(pathname);
  if (hostname === PUBLIC_HOST && !publicPage) {
    return `https://${ADMIN_HOST}${pathname}${search}${hash}`;
  }
  return null;
}
